import { useEffect, useState } from "react";
import { api, formatARS } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Wallet, Award, Users as UsersIcon } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { es } from "date-fns/locale";

const RANGES = [
  { key: "week", label: "Semana" },
  { key: "month", label: "Mes" },
  { key: "year", label: "Año" },
  { key: "all", label: "Todo" },
];

export default function Earnings() {
  const [range, setRange] = useState("month");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [courts, setCourts] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, b, c, u] = await Promise.all([
        api.get("/stats"),
        api.get("/bookings"),
        api.get("/courts"),
        api.get("/customers"),
      ]);
      setStats(s.data);
      setBookings(b.data);
      setCourts(c.data);
      setCustomers(u.data);
    })();
  }, []);

  if (!stats) return <div className="text-zinc-500 font-heading">CARGANDO...</div>;

  const today = new Date();
  const inRange = (dateStr) => {
    const d = new Date(dateStr + "T00:00");
    if (range === "all") return true;
    if (range === "week") return d >= startOfWeek(today, { weekStartsOn: 1 }) && d <= endOfWeek(today, { weekStartsOn: 1 });
    if (range === "month") return d >= startOfMonth(today) && d <= endOfMonth(today);
    if (range === "year") return d >= startOfYear(today) && d <= endOfYear(today);
    return true;
  };

  const confirmed = bookings.filter((b) => b.status === "confirmed" && inRange(b.date));
  const total = confirmed.reduce((sum, b) => sum + (b.price_ars || 0), 0);
  const avgTicket = confirmed.length > 0 ? total / confirmed.length : 0;

  // By court
  const courtMap = Object.fromEntries(courts.map((c) => [c.court_id, c]));
  const byCourt = {};
  confirmed.forEach((b) => {
    const cn = b.court_name || "—";
    byCourt[cn] = byCourt[cn] || { name: cn, income: 0, count: 0, sport: courtMap[b.court_id]?.sport || "futbol" };
    byCourt[cn].income += b.price_ars || 0;
    byCourt[cn].count += 1;
  });
  const courtsData = Object.values(byCourt).sort((a, b) => b.income - a.income);

  // By sport (pie)
  const sportIncome = confirmed.reduce((acc, b) => {
    const sport = courtMap[b.court_id]?.sport || "futbol";
    acc[sport] = (acc[sport] || 0) + (b.price_ars || 0);
    return acc;
  }, {});
  const sportData = [
    { name: "Fútbol", value: sportIncome.futbol || 0, color: "#10B981" },
    { name: "Pádel", value: sportIncome.padel || 0, color: "#F59E0B" },
  ].filter((s) => s.value > 0);

  // Daily income (last 30 days for month, last 7 for week)
  const days = range === "week" ? 7 : range === "year" ? 12 : 30;
  const daily = {};
  if (range === "year") {
    for (let i = 0; i < 12; i++) {
      const d = new Date(today.getFullYear(), i, 1);
      daily[format(d, "MMM", { locale: es })] = 0;
    }
    confirmed.forEach((b) => {
      const key = format(new Date(b.date + "T00:00"), "MMM", { locale: es });
      if (key in daily) daily[key] += b.price_ars || 0;
    });
  } else {
    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      daily[format(d, "dd/MM")] = 0;
    }
    confirmed.forEach((b) => {
      const key = format(new Date(b.date + "T00:00"), "dd/MM");
      if (key in daily) daily[key] += b.price_ars || 0;
    });
  }
  const dailyData = Object.entries(daily).map(([date, income]) => ({ date, income }));

  // Top customers (top 5)
  const topCustomers = [...customers].sort((a, b) => (b.total_spent || 0) - (a.total_spent || 0)).slice(0, 5);

  const kpis = [
    { icon: Wallet, label: "Ingreso total", value: formatARS(total), hint: `${confirmed.length} turnos confirmados` },
    { icon: TrendingUp, label: "Ticket promedio", value: formatARS(avgTicket), hint: "por turno confirmado" },
    { icon: Award, label: "Mejor cancha", value: courtsData[0]?.name || "—", hint: courtsData[0] ? formatARS(courtsData[0].income) : "sin datos" },
    { icon: UsersIcon, label: "Clientes activos", value: customers.length, hint: "registrados en total" },
  ];

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-2">// Dinero</div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight">GANANCIAS</h1>
        </div>
        <Tabs value={range} onValueChange={setRange}>
          <TabsList className="bg-[#141414] border border-[#262626]">
            {RANGES.map((r) => (
              <TabsTrigger key={r.key} value={r.key} className="uppercase tracking-widest text-xs data-[state=active]:bg-emerald-500 data-[state=active]:text-black" data-testid={`range-${r.key}`}>
                {r.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k, i) => {
          const Icon = k.icon;
          return (
            <div key={i} className="bg-[#141414] border border-[#262626] rounded-lg p-6 hover:border-emerald-500/40 transition" data-testid={`kpi-${i}`}>
              <Icon className="h-5 w-5 text-emerald-400 mb-3" strokeWidth={1.5} />
              <div className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-1">{k.label}</div>
              <div className="font-heading text-3xl truncate">{k.value}</div>
              <div className="text-xs text-zinc-500 mt-1">{k.hint}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2 bg-[#141414] border border-[#262626] rounded-lg p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">// Evolución de ingresos</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={dailyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }} />
              <YAxis stroke="#71717a" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
              <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 6 }} formatter={(v) => formatARS(v)} />
              <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} dot={{ fill: "#10B981", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-lg p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">// Por deporte</div>
          {sportData.length === 0 ? (
            <div className="text-zinc-500 text-sm h-[200px] flex items-center justify-center">Sin datos todavía</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={sportData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2} dataKey="value">
                  {sportData.map((s, i) => <Cell key={i} fill={s.color} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 6 }} formatter={(v) => formatARS(v)} />
                <Legend wrapperStyle={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 2 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#141414] border border-[#262626] rounded-lg p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">// Por cancha</div>
          {courtsData.length === 0 ? (
            <div className="text-zinc-500 text-sm h-[200px] flex items-center justify-center">Sin turnos confirmados en este período</div>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(180, courtsData.length * 44)}>
              <BarChart data={courtsData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis type="number" stroke="#71717a" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
                <YAxis dataKey="name" type="category" stroke="#71717a" tick={{ fontSize: 11 }} width={90} />
                <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", borderRadius: 6 }} formatter={(v) => formatARS(v)} />
                <Bar dataKey="income" fill="#10B981" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-[#141414] border border-[#262626] rounded-lg p-6">
          <div className="text-xs uppercase tracking-[0.3em] text-emerald-400 mb-6">// Top 5 clientes</div>
          {topCustomers.length === 0 ? (
            <div className="text-zinc-500 text-sm h-[200px] flex items-center justify-center">Sin clientes todavía</div>
          ) : (
            <div className="space-y-2" data-testid="top-customers">
              {topCustomers.map((c, i) => (
                <div key={c.customer_id} className="flex items-center gap-3 p-3 bg-[#0A0A0A] rounded border border-[#262626]">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-xs font-bold">#{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold truncate">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.confirmed_count} turnos · {c.phone}</div>
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 font-heading">
                    {formatARS(c.total_spent)}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
